package org.multiplatform.kotfolio.pages.compose.viewmodel

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/viewmodel/viewmodel")
@Composable
fun ViewModelComposePage() {
    BlogLayout(
        PageProperties(
            "ViewModel",
            "The ViewModel class is a business logic or screen level state holder. It exposes state to the UI and encapsulates related business logic. Its principal advantage is that it caches state and persists it through configuration changes."
        ),
        BlogProperties("Jetpack Compose", "ViewModel")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            //linkedin.com/in/satyam-raikar-477198181/?skipRedirect=true
            SpanText(
                text = "Lifecycle 2.11 - (Subcomponent Scoping - Composition-Aware Lifecycle - ViewModel Scoping - Scoping APIs - State Restoration - Dynamic Arguments)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Subcomponent Scoping - Assign dedicated ViewModels to specific Ul components such as list items, dialogs, bottom sheets, and reusable widgets.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition-Aware Lifecycle - ViewModels live only as long as their Composable remains in the composition. When a component leaves the Ul, its ViewModel is automatically cleared.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Restoration - Combine with remember SaveableStateHolder to preserve and restore local Ul state seamlessly when components re-enter the composition.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dynamic Arguments - Pass runtime data such as IDs and configuration values directly into component-scoped ViewModels using CreationExtras.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Lifecycle (JetBrains KMP wrappers with iOS support)
                    implementation("org.jetbrains.androidx.lifecycle:lifecycle-viewmodel:2.11.0-beta01")
                    implementation("org.jetbrains.androidx.lifecycle:lifecycle-runtime-compose:2.11.0-beta01")
                    
                    implementation("org.jetbrains.androidx.lifecycle:lifecycle-viewmodel-compose:2.11.0-beta01")
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    androidx-lifecycle-compose = "2.10.0"
                    androidx-activity-compose = "1.13.0"
                    
                    androidx-lifecycle-viewmodel-compose = { module = "org.jetbrains.androidx.lifecycle:lifecycle-viewmodel-compose", version.ref = "androidx-lifecycle-compose" }
                    androidx-lifecycle-runtime-compose = { module = "org.jetbrains.androidx.lifecycle:lifecycle-runtime-compose", version.ref = "androidx-lifecycle-compose" }
                    
                    import androidx.lifecycle.viewmodel.compose.viewModel
                    
                    @Composable
                    fun App(
                        appViewModel: AppViewModel = viewModel { AppViewModel() }
                    ) {  }
                    
                    class AppViewModel : ViewModel() { }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModelStore is a class provided by the Android Jetpack library that stores ViewModel instances. It acts as a container that holds ViewModels and manages their lifecycles, ensuring they survive configuration changes like screen rotations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Until now, ViewModelStore scoping was tied to navigation destinations, activities or fragments. There was no clean way to scope a ViewModel to an arbitrary part of your UI (such as a Pager page, a LazyList item, or a custom layout) without building your own ViewModelStoreOwner from scratch.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "New APIs close that gap - Stop Building God ViewModels with “One Screen = One ViewModel”",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using the New Android ViewModel APIs for Lifecycle-Aware Compose Architecture.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.NEW_VIEWMODEL_API,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "New ViewModel API"
            )

            SpanText(
                text = "Why This Matters",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In classic Android development, a ViewModel was usually scoped to an Activity, Fragment, or navigation destination.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "But in modern Jetpack Compose apps, a single screen often contains:",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "dynamically appearing UI",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "nested composables",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "pagers",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "bottom sheets",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "temporary feature flows",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In those cases, scoping everything to the entire screen becomes too broad.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sometimes you want a ViewModel to live only as long as a specific composable exists.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "The Problem with Traditional ViewModel Scoping",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A normal viewModel() call uses the nearest ViewModelStoreOwner.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    Activity
                        └─ NavBackStackEntry
                        └─ Entire Screen
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModels survive longer than necessary.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "temporary UI state remains in memory",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "pager pages may unintentionally share state",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "cleanup timing becomes unclear",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            //Using the rememberViewModelStoreOwner API, ViewModels can be scoped to specific subcomponents (e.g., a specific LazyList item, a bottom sheet, or a sub-page).
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "You can now create child-scoped ViewModels whose lifecycle matches their position in the UI tree — and they’re automatically cleared when that composable leaves composition.",
                    modifier = Modifier.fontSize(FontSize.Medium)
                )
            }

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberViewModelStoreOwner() - A store owner tied to the composable's lifecycle",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberViewModelStoreProvider - For multiple independent scopes (think Pager pages or LazyList items)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberViewModelStoreProvider() hoists a provider at the parent level, while rememberViewModelStoreOwner(provider, key) creates a unique ViewModelStoreOwner per UI element. ViewModels survive configuration changes and are automatically cleared when their corresponding Composable is permanently removed from the hierarchy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Scoping a ViewModel to a Specific Composable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The first API introduces a ViewModel lifecycle tied directly to a composable call site.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun RememberViewModelStoreOwnerSample() {
                        // Create a ViewModelStoreOwner scoped to this specific call site.
                        // When this composable leaves the composition,
                        // the associated ViewModelStore will be cleared.
                        val scopedOwner = rememberViewModelStoreOwner() // *

                        CompositionLocalProvider(LocalViewModelStoreOwner provides scopedOwner) {
                            // This ViewModel is scoped to `scopedOwner`.
                            // It will survive configuration changes but will be cleared when
                            // the composable is removed from the UI tree.
                            val viewModel = viewModel { TestViewModel("scoped_data") }

                            // Use the ViewModel
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    Screen
                        └─ RememberViewModelStoreOwnerSample()
                           └─ rememberViewModelStoreOwner()
                               └─ LocalViewModelStoreOwner
                                   └─ TestViewModel
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Parent-scoped ViewModel — behaves as before.
                    val parentViewModel = viewModel<ParentViewModel>()

                    // Create a child-scoped owner.
                    val childOwner = rememberViewModelStoreOwner()

                    // ViewModel scoped to the child owner.
                    val childViewModel = viewModel<ChildViewModel>(childOwner)

                    // Optionally propagate the owner down the composition tree.
                    CompositionLocalProvider(LocalViewModelStoreOwner provides childOwner) {
                        // Composables here resolve ViewModels from childOwner.
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Independent ViewModels for Pagers, Tabs, and Lazy Lists",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun RememberViewModelStoreProviderSample() {
                        val storeProvider = rememberViewModelStoreProvider() // *
                        val pages = listOf("Page 1", "Page 2", "Page 3")

                        HorizontalPager(pageCount = pages.size) { page ->
                            // Create a ViewModelStoreOwner for the specific page 
                            // using the provider.
                            val pageOwner = rememberViewModelStoreOwner(
                                provider = storeProvider,
                                key = page
                            )

                            CompositionLocalProvider(LocalViewModelStoreOwner provides pageOwner) {
                                val pageViewModel = viewModel {
                                    TestViewModel(pages[page])
                                }

                                // Use pageViewModel
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Hoist the parent provider outside the Pager.
                    val provider = rememberViewModelStoreProvider()
                    val pagerState = rememberPagerState(pageCount = {5})
                    
                    HorizontalPager (state = pagerState) { page ->
                        // Create an owner scoped to the current page.
                        val storeOwner = rememberViewModelStoreOwner(provider, key = page)
                    
                        // Provide the scoped owner to this composition.
                        CompositionLocalProvider(LocalViewModelStoreOwner provides storeOwner) {
                            // ViewModel is now scoped specifically to this page.
                            val viewModel: PageViewModel = viewModel()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun MyComposable() {
                        NavDisplay(
                            backStack = backStack,
                            entryDecorators =
                                listOf(
                                    rememberSaveableStateHolderNavEntryDecorator(),
                                    // Add this line to automatically scope ViewModels to each entry
                                    rememberViewModelStoreNavEntryDecorator(),
                                ),
                            entryProvider = entryProvider {
                                // Add your entries here
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Nested Saved State
                    
                    val saveableStateHolder = rememberSaveableStateHolder()
                    val provider = rememberViewModelStoreProvider()
                    val pagerState = rememberPagerState(pageCount = { 3 }, initialPage = 0)
                    
                    HorizontalPager(
                        state = pagerState,
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxSize(),
                        userScrollEnabled = false,
                    ) { page ->
                        saveableStateHolder.SaveableStateProvider(key = page) {
                            val storeOwner = rememberViewModelStoreOwner(provider, key = page)
                            CompositionLocalProvider(LocalViewModelStoreOwner provides storeOwner) {
                                when (page) { }
                            }
                        }  
                    }  
                    
                    //2
                    @Composable
                    fun LazyColumnWithScopedViewModels() {
                        val provider = rememberViewModelStoreProvider()

                        LazyColumn {
                            items(
                                count = 20,
                                key = { it } // IMPORTANT: stable key for correct scoping
                            ) { index ->

                                // Create a ViewModelStoreOwner per item
                                val storeOwner = rememberViewModelStoreOwner(
                                    provider = provider,
                                    key = index
                                )

                                CompositionLocalProvider(
                                    LocalViewModelStoreOwner provides storeOwner
                                ) {
                                    val viewModel: ItemViewModel = viewModel()
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Scoped ViewModels Dialog 
                    
                    import androidx.compose.runtime.*
                    import androidx.compose.ui.window.Dialog
                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.viewmodel.compose.LocalViewModelStoreOwner
                    import androidx.lifecycle.viewmodel.compose.rememberViewModelStoreOwner
                    import androidx.lifecycle.viewmodel.compose.viewModel
                    import androidx.compose.runtime.CompositionLocalProvider

                    class DialogViewModel : ViewModel() {

                        init {
                            println("DialogViewModel created")
                        }

                        override fun onCleared() {
                            println("DialogViewModel cleared")
                        }

                        var counter by mutableStateOf(0)
                            private set

                        fun increment() {
                            counter++
                        }
                    }

                    @Composable
                    fun DialogExample() {
                        var showDialog by remember { mutableStateOf(false) }

                        Button(
                            onClick = { showDialog = true }
                        ) {
                            Text("Open Dialog")
                        }

                        if (showDialog) {
                            Dialog(
                                onDismissRequest = {
                                    showDialog = false
                                }
                            ) {

                                val dialogOwner = rememberViewModelStoreOwner()

                                CompositionLocalProvider(
                                    LocalViewModelStoreOwner provides dialogOwner
                                ) {
                                    val vm: DialogViewModel = viewModel()

                                    Column {
                                        Text("Count: ${vm.counter}")

                                        Button(
                                            onClick = vm::increment
                                        ) {
                                            Text("Increment")
                                        }

                                        Button(
                                            onClick = {
                                                showDialog = false
                                            }
                                        ) {
                                            Text("Close")
                                        }
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Scoped ViewModels (Parent and Child ViewModel Different) 
                    
                    @Composable
                    private fun ScopedDialog(onDismiss: () -> Unit) {
                        val viewModelStoreOwner = rememberViewModelStoreOwner()
                        val dialogViewModel = koinViewModel<DialogViewModel>(viewModelStoreOwner = viewModelStoreOwner)
                        
                        Dialog(onDismissRequest = onDismiss) { 
                        }
                    }
                    """.trimIndent()
            )

            //https://gist.github.com/ardakazanci
            KotlinCode(
                code = $$"""
                    class MainActivity : ComponentActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            enableEdgeToEdge()
                            setContent {
                                ScopedViewModelTheme {
                                    Scaffold(modifier = Modifier.fillMaxSize()) { contentPadding ->
                                        ScopedViewModelDemo(
                                            modifier = Modifier.padding(contentPadding)
                                        )
                                    }
                                }
                            }
                        }
                    }

                    @Composable
                    private fun ScopedViewModelDemo(modifier: Modifier = Modifier) {
                        val provider = rememberViewModelStoreProvider()
                        var pageIds by rememberSaveable { mutableStateOf(listOf(1, 2, 3)) }
                        var nextPageId by rememberSaveable { mutableStateOf(4) }
                        val pagerState = rememberPagerState(pageCount = { pageIds.size })

                        LaunchedEffect(pageIds.size) {
                            if (pageIds.isNotEmpty() && pagerState.currentPage > pageIds.lastIndex) {
                                pagerState.scrollToPage(pageIds.lastIndex)
                            }
                        }

                        Column(
                            modifier = modifier
                                .fillMaxSize()
                                .background(MaterialTheme.colorScheme.background)
                                .padding(horizontal = 20.dp, vertical = 16.dp),
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            DemoHeader(
                                pageCount = pageIds.size,
                                currentPage = pagerState.currentPage + 1,
                                onAddPage = {
                                    pageIds = pageIds + nextPageId
                                    ScopedVmEvents.record("Page key $nextPageId entered the pager")
                                    nextPageId += 1
                                },
                                onRemoveCurrentPage = {
                                    if (pageIds.size > 1) {
                                        val currentIndex = pagerState.currentPage.coerceIn(pageIds.indices)
                                        val removedKey = pageIds[currentIndex]
                                        pageIds = pageIds.toMutableList().also { it.removeAt(currentIndex) }
                                        provider.clearKey(removedKey)
                                        ScopedVmEvents.record("Page key $removedKey marked for cleanup")
                                    }
                                },
                                canRemove = pageIds.size > 1
                            )

                            HorizontalPager(
                                state = pagerState,
                                key = { index -> pageIds[index] },
                                pageSpacing = 16.dp,
                                contentPadding = PaddingValues(horizontal = 4.dp),
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .weight(1f)
                            ) { pageIndex ->
                                val pageKey = pageIds[pageIndex]
                                val storeOwner = rememberViewModelStoreOwner(provider, key = pageKey)
                                val pageViewModel = viewModel<ScopedPageViewModel>(
                                    viewModelStoreOwner = storeOwner,
                                    key = "scoped-page"
                                ) {
                                    ScopedPageViewModel()
                                }

                                PageCard(
                                    pageNumber = pageIndex + 1,
                                    pageKey = pageKey,
                                    viewModel = pageViewModel,
                                    modifier = Modifier.fillMaxSize()
                                )
                            }

                            LifecycleLog(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(132.dp)
                            )
                        }
                    }

                    @Preview(showBackground = true)
                    @Composable
                    private fun ScopedViewModelPreview() {
                        ScopedViewModelTheme {
                            Surface {
                                Column(Modifier.padding(16.dp)) {
                                    Text(
                                        text = "Scoped ViewModel",
                                        style = MaterialTheme.typography.headlineSmall
                                    )
                                    Spacer(Modifier.height(12.dp))
                                    Text(
                                        text = "Run the app to create ViewModelStoreOwner scopes inside the Pager.",
                                        style = MaterialTheme.typography.bodyMedium
                                    )
                                }
                            }
                        }
                    }

                    @Composable
                    private fun DemoHeader(
                        pageCount: Int,
                        currentPage: Int,
                        onAddPage: () -> Unit,
                        onRemoveCurrentPage: () -> Unit,
                        canRemove: Boolean,
                        modifier: Modifier = Modifier
                    ) {
                        Column(
                            modifier = modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column {
                                    Text(
                                        text = "Scoped ViewModel",
                                        style = MaterialTheme.typography.headlineMedium,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                    Text(
                                        text = "Pager page $currentPage of $pageCount",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }

                                AssistChip(
                                    onClick = {},
                                    label = { Text("2.11.0-alpha02") }
                                )
                            }

                            Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                                Button(onClick = onAddPage) {
                                    Text("Add page")
                                }
                                OutlinedButton(
                                    onClick = onRemoveCurrentPage,
                                    enabled = canRemove
                                ) {
                                    Text("Remove current")
                                }
                            }
                        }
                    }

                    @Composable
                    private fun PageCard(
                        pageNumber: Int,
                        pageKey: Int,
                        viewModel: ScopedPageViewModel,
                        modifier: Modifier = Modifier
                    ) {
                        ElevatedCard(
                            modifier = modifier,
                            shape = RoundedCornerShape(8.dp),
                            colors = CardDefaults.elevatedCardColors(
                                containerColor = MaterialTheme.colorScheme.surfaceContainerLow
                            )
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(20.dp),
                                verticalArrangement = Arrangement.spacedBy(18.dp)
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column {
                                        Text(
                                            text = "Page $pageNumber",
                                            style = MaterialTheme.typography.headlineSmall,
                                            fontWeight = FontWeight.SemiBold
                                        )
                                        Text(
                                            text = "Store key $pageKey",
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    }

                                    Box(
                                        modifier = Modifier
                                            .clip(CircleShape)
                                            .background(MaterialTheme.colorScheme.primaryContainer)
                                            .padding(horizontal = 14.dp, vertical = 8.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = "VM ${viewModel.instanceNumber}",
                                            style = MaterialTheme.typography.labelLarge,
                                            color = MaterialTheme.colorScheme.onPrimaryContainer
                                        )
                                    }
                                }

                                HorizontalDivider()

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    StatBlock(
                                        label = "Counter",
                                        value = viewModel.counter.toString(),
                                        modifier = Modifier.weight(1f)
                                    )
                                    StatBlock(
                                        label = "Touches",
                                        value = viewModel.touchCount.toString(),
                                        modifier = Modifier.weight(1f)
                                    )
                                }

                                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                                    Button(onClick = viewModel::incrementCounter) {
                                        Text("Increment")
                                    }
                                    OutlinedButton(onClick = viewModel::markTouched) {
                                        Text("Touch VM")
                                    }
                                    TextButton(onClick = viewModel::reset) {
                                        Text("Reset")
                                    }
                                }

                                Text(
                                    text = "Active scoped owner: key $pageKey, VM ${viewModel.instanceNumber}",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    }

                    @Composable
                    private fun StatBlock(
                        label: String,
                        value: String,
                        modifier: Modifier = Modifier
                    ) {
                        Card(
                            modifier = modifier,
                            shape = RoundedCornerShape(8.dp),
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.surface
                            )
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalArrangement = Arrangement.spacedBy(6.dp)
                            ) {
                                Text(
                                    text = label,
                                    style = MaterialTheme.typography.labelLarge,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                                Text(
                                    text = value,
                                    style = MaterialTheme.typography.displaySmall,
                                    fontWeight = FontWeight.SemiBold
                                )
                            }
                        }
                    }

                    @Composable
                    private fun LifecycleLog(modifier: Modifier = Modifier) {
                        Card(
                            modifier = modifier,
                            shape = RoundedCornerShape(8.dp),
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.surface
                            )
                        ) {
                            Column(
                                modifier = Modifier.padding(14.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = "Lifecycle log",
                                        style = MaterialTheme.typography.titleSmall,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                    TextButton(onClick = ScopedVmEvents::clear) {
                                        Text("Clear")
                                    }
                                }

                                val recentEvents = ScopedVmEvents.events.takeLast(4).asReversed()
                                if (recentEvents.isEmpty()) {
                                    Text(
                                        text = "No events yet",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                } else {
                                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                                        recentEvents.forEach { event ->
                                            Text(
                                                text = event,
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    private class ScopedPageViewModel : ViewModel() {
                        val instanceNumber = nextInstanceNumber()

                        var counter by mutableIntStateOf(0)
                            private set

                        var touchCount by mutableIntStateOf(0)
                            private set

                        init {
                            ScopedVmEvents.record("VM $instanceNumber created")
                        }

                        fun incrementCounter() {
                            counter += 1
                        }

                        fun markTouched() {
                            touchCount += 1
                        }

                        fun reset() {
                            counter = 0
                            touchCount = 0
                        }

                        override fun onCleared() {
                            ScopedVmEvents.record("VM $instanceNumber cleared")
                        }

                        companion object {
                            private var nextInstance = 1

                            private fun nextInstanceNumber(): Int = nextInstance++
                        }
                    }

                    private object ScopedVmEvents {
                        val events = mutableStateListOf<String>()

                        fun record(event: String) {
                            events += event
                            if (events.size > 20) {
                                events.removeRange(0, events.size - 20)
                            }
                        }

                        fun clear() {
                            events.clear()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Inheritance",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inheritance represents an “Is-A” relationship between types. It allows one class to reuse behavior from another by extending it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While inheritance can be powerful, it introduces structural coupling between parent and child classes, which can make large hierarchies harder to evolve safely.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When inheritance makes sense",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Extending framework components: (ViewModel, RoomDatabase, BroadcastReceiver, etc.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modeling a true taxonomic hierarchy",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Defining sealed hierarchies or polymorphic models",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Composition: The “Has-A” Relationship",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition builds complex objects by combining smaller, independent components. “A ViewModel is a BaseViewModel”",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Jetpack Compose and Composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose encourages building UI through function composition rather than UI inheritance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    Column {
                        UserAvatar()
                        UserName()
                        FollowButton()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Delegation: Composition Without Boilerplate",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "class delegation using the by keyword.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Delegation allows a class to implement an interface while forwarding the implementation to another object, eliminating repetitive boilerplate.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Internally this is still composition, but externally the class behaves as if it implements the interface itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When delegation is useful",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multiple classes must implement the same interface behavior.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to avoid base classes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to reuse logic without inheritance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    interface Loadable {
                        val isLoading: StateFlow<Boolean>
                        fun setLoading(loading: Boolean)
                    }

                    class LoadableDelegate : Loadable {
                        private val _isLoading = MutableStateFlow(false)
                        override val isLoading = _isLoading.asStateFlow()
                        override fun setLoading(loading: Boolean) {
                            _isLoading.value = loading
                        }
                    }
                    
                    class ProfileViewModel(
                        private val loadable: Loadable = LoadableDelegate()
                    ) : ViewModel(), Loadable by loadable {
                      
                        fun fetchProfile() {
                            setLoading(true)
                            viewModelScope.launch {
                                // Other logic
                                setLoading(false)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Delegated State Holders for Compose Features",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This allows feature modules to reuse UI logic without inheritance or duplicated state management.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    interface PaginationState {
                        val isLoadingMore: StateFlow<Boolean>
                        suspend fun loadNextPage()
                    }
                    
                    class PaginationDelegate(
                        private val repository: ArticleRepository
                    ) : PaginationState {
                        private val _isLoadingMore = MutableStateFlow(false)
                        override val isLoadingMore = _isLoadingMore.asStateFlow()
                        override suspend fun loadNextPage() {
                            if (_isLoadingMore.value) return
                            _isLoadingMore.value = true
                            try {
                                repository.fetchNextPage()
                            } finally {
                                _isLoadingMore.value = false
                            }
                        }
                    }
                    
                    //Delegate ViewModel
                    class ArticleListViewModel(
                        repository: ArticleRepository
                    ) : ViewModel(), PaginationState by PaginationDelegate(repository)
                    """.trimIndent()
            )

            SpanText(
                text = "State Restoration?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State restoration refers to saving and restoring the Ul state of an app during events like:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Configuration changes: Screen rotation or dark mode toggle.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Process death: When the app is killed and restarted by the system.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, managing state restoration is simpler compared to traditional Views, thanks to its declarative approach.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://medium.com/@appdevinsights/difference-between-viewmodel-and-androidviewmodel-097faf56160e
            //https://github.com/evodartstudios/glyph-dial.git
            SpanText(
                text = "AndroidViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The AndroidViewModel is a specific implementation of the base ViewModel class in Android Jetpack that has access to the application's Context. This allows it to perform operations that require a context, such as accessing string resources or system services, databases, SharedPreferences, without causing memory leaks. Holds a reference to the application context, which is less likely to cause memory leaks compared to holding an activity or fragment context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Requires an Application instance as a parameter in its constructor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/romreviewer/NoteItUP.git
            KotlinCode(
                code = """
                    class WeatherViewModel(
                        application: Application,
                    ): AndroidViewModel(application) {
                        private val fusedLocationClient: FusedLocationProviderClient =
                            LocationServices.getFusedLocationProviderClient(application)
                    }
                    
                    //koin di
                    actual class LocationService(private val context: Context) {

                        private val fusedLocationClient: FusedLocationProviderClient =
                            LocationServices.getFusedLocationProviderClient(context)
                    
                    }
                    
                    single { LocationService(androidContext()) }
                    """.trimIndent()
            )

            SpanText(
                text = "Jetpack Compose and ViewModel contract",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel is to prepare and expose UI state as StateFlow<> (or StateFlow<List<>>) which compose collects with collectAsStateWithLifecycle(); the ViewModel receives UI events from Compose via functions; the ViewModel never holds a reference to the Compose UI - this one - directional data flow is what makes Compose screens independently testable. That's it. It converts raw domain data into something a Composable can render and it routes user events to the intended location.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Its primary goal is to store and manage UI-related data in a lifecycle-conscious way. It is tied to a lifecycle — usually an Activity or a Fragment — but will survive configuration changes like screen rotations or UI rebuilds.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel is a lifecycle-aware component designed to store and manage UI-related data in a way that survives configuration changes. Combined with StateFlow, a Kotlin construct for state management, you can create reactive streams of data that are easy to observe in Jetpack Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Android system automatically clears the ViewModel when its associated lifecycle is finished. This ensures the data is cleared only when it’s no longer needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose application, you can easily integrate ViewModels to ensure your UI is always reflecting the most up-to-date state, without manually handling lifecycle events.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //1
                    androidx-lifecycle = "2.9.6"

                    androidx-lifecycle-viewmodel = { group = "org.jetbrains.androidx.lifecycle", name = "lifecycle-viewmodel", version.ref = "androidx-lifecycle" }
                    androidx-lifecycle-runtime-compose = { group = "org.jetbrains.androidx.lifecycle", name = "lifecycle-runtime-compose", version.ref = "androidx-lifecycle" }

                    // Lifecycle & ViewModel
                    implementation(libs.androidx.lifecycle.viewmodel)
                    implementation(libs.androidx.lifecycle.runtime.compose)
                    
                    //2
                    lifecycleViewModel = "2.10.0"
                    lifecycle-viewmodel = { module = "androidx.lifecycle:lifecycle-viewmodel", version.ref = "lifecycleViewModel"}
                    
                    //Access ViewModel
                    val viState by vm.viState.collectAsStateWithLifecycle()
                    """.trimIndent()
            )

            SpanText(
                text = "Create ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "viewModel()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "koinViewModel()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "hiltViewModel()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Voyager - ScreenModel()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Features of ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lifecycle Awareness",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The ViewModel is scoped to the lifecycle of an Activity or Fragment. It is automatically destroyed when the associated UI component is no longer in use, such as when the user navigates away from the screen.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Persistence Across Configuration Changes",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Unlike Activity or Fragment, which are destroyed and recreated during configuration changes, the ViewModel retains its state, preventing data loss and avoiding repetitive re-fetching of data.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel allows persistence through both the state that a ViewModel holds, and the operations that a ViewModel triggers. This caching means that you don’t have to fetch data again through common configuration changes, such as a screen rotation.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Separation of Concerns",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The ViewModel helps separate UI-related logic from business logic, promoting cleaner and more maintainable code. The UI layer observes the ViewModel for updates, making it easier to implement reactive programming principles.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "The lifecycle of a ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            WebImage(src = Resources.Images.VIEWMODEL_LIFECYCLE_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Lifecycle of ViewModel")

            SpanText(
                text = "ViewModel Internal Working",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lifecycle Awareness",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel survives configuration changes by being stored in a ViewModelStore tied to the Activity or Fragment",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is typically used with viewModelScope to launch coroutines that are automatically canceled when the ViewModel is cleared.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data Exposure",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It exposes data via LiveData or StateFlow, allowing the UI to reactively update. This pattern reduces memory leaks and ensures UI consistency.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Collect Data from ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsState (Recommended for Ul State): Automatically collects StateFlow and triggers recomposition when data changes, making it ideal for Ul state management.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectAsStateWithLifecycle (Best for Performance): Similar to collectAsState, but lifecycle-aware, preventing unnecessary recompositions and improving efficiency.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect (For One-Time Events & Side Effects): collecting non-Ul state, such as analytics or event-based flows, without triggering recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState (For Long-Running Operations): Handling asynchronous operations while keeping track of initial and updated states efficiently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope (For Manual Data Fetching): User-triggered data fetching inside a coroutine without causing recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Differences Between Jetpack ViewModel and MVVM ViewModel?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Jetpack ViewModel provides lifecycle-aware state management, primarily focusing on retaining UI-related state through configuration changes. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVVM ViewModel, as defined by the MVVM architecture, acts as a facilitator between the View and the Model, handling binding and ensuring the UI is only concerned with displaying data. MVVM ViewModel emphasizes the binding of data and commands between the View and the Model, enabling a more declarative and passive UI",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Can Multiple Views Use the Same ViewModel in MVVM?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Yes! That’s the power of shared ViewModels using activityViewModels() or a singleton-scoped ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ViewModel State Private (Single Source of Truth)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //1
                    private val _state = MutableStateFlow(UiState())
                    val state: StateFlow<UiState> = _state
                    
                    //2
                    private val _uiState by lazy { MutableStateFlow<HomeUiState>(HomeUiState.Loading) }
                    val uiState: StateFlow<HomeUiState>
                        get() = _uiState.asStateFlow()
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Single Source of Truth",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The ViewModel must be the only place allowed to change the state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI observes the state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI never modifies it.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Why private?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Prevents accidental updates from UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Makes state changes predictable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Easier debugging & testing",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "All state updates go through ViewModel functions only.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ViewModelStoreOwner",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/RznNike/DemoKMP.git (Good API Calling (webSocketManager, ktorfit, Common UseCase))
            KotlinCode(
                code = """
                     val viewModel = viewModel { HTTPExampleViewModel() }
                     
                     class HTTPExampleViewModel : BaseUiViewModel<HTTPExampleViewModel.UiState>() { }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModelProvider",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/XDdevv/OctoberMiniChallengesKMP.git
            KotlinCode(
                code = """
                    //Support Compose, KMM
                    
                    import androidx.compose.runtime.Composable
                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.ViewModelProvider
                    import androidx.lifecycle.viewmodel.CreationExtras
                    import androidx.lifecycle.viewmodel.compose.viewModel
                    import kotlin.reflect.KClass

                    @Composable
                    inline fun <reified VM : ViewModel> composeViewModel(
                        crossinline creator: () -> VM
                    ): VM {
                        val factory = object : ViewModelProvider.Factory {
                            override fun <T : ViewModel> create(modelClass: KClass<T>, extras: CreationExtras): T {
                                @Suppress("UNCHECKED_CAST")
                                return creator() as T
                            }
                        }
                        return viewModel(factory = factory)
                    }
                    
                     HaunterThemeRoot(viewModel = composeViewModel { HauntedThemeViewModel() })
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Kotlin Multiplatform 
                    
                    //1
                    class FoodViewModel : ViewModel() { 
                        companion object {
                            val foodViewModelFactory = viewModelFactory {
                                initializer {
                                    FoodViewModel()
                                }
                            }
                        }
                    }
                    
                    val foodViewModel: FoodViewModel = viewModel(factory = foodViewModelFactory)
                    
                    //2 Multiple ViewModels in the same factory
                    //androidMain
                    viewModel = viewModel(factory = viewModelFactory {
                        addInitializer(MissedMessagesViewModel::class, initializer = {
                            MissedMessagesViewModel(AndroidMissedMessagedRepository())
                        })
                    })
                    
                    //iosMain
                    fun MainViewController() = ComposeUIViewController {
                        App(viewModel = viewModel(factory = viewModelFactory {
                            addInitializer(StillConnectedViewModel::class, initializer = {
                                StillConnectedViewModel(IOSConnectivityManager())
                            })
                        }))
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModel init",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Not good way
                    class FooViewModel(
                        private val repository: Repository
                    ) : ViewModel() {

                        private val _state = MutableStateFlow<String?>(null)
                        val state: StateFlow<String?> = _state

                        init {
                            viewModelScope.launch {
                                val data = repository.fetchData()
                                _state.value = data
                            }
                        }
                    }
                    
                    //Correct way
                    class FooViewModel(
                        private val repository: Repository
                    ) : ViewModel() {

                        private val _state = MutableStateFlow<String?>(null)
                        val state: StateFlow<String?> = _state

                        init {
                            viewModelScope.launch(Dispatchers.IO) {
                                val data = repository.fetchData()
                                _state.value = data
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "remember ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    ❌ remember {} does not survive configuration changes (rotation, process recreation).It breaks lifecycle awareness.
                   @Composable
                   fun CoffeeMachineScreen(
                       viewModel: CoffeeMachineViewModel = remember { CoffeeMachineViewModel() }
                   ) { }
                   
                   class CoffeeMachineViewModel : ViewModel() { }
                   //CoffeeMachineScreen() No need DI
                   
                   ✅viewModel() from lifecycle-compose
                   @Composable
                   fun CoffeeMachineScreen(
                       viewModel: CoffeeMachineViewModel = viewModel()
                   ) { }
                """.trimIndent()
            )

            SpanText(
                text = "context avoid",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Domain UseCases - Pure Kotlin (no Android imports)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Ties domain logic to Android",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Breaks testability (unit tests need Android)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Violates Dependency Rule",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Makes domain non-reusable (KMP, backend, etc.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Pure Repository interfaces",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModels (should only receive abstractions)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Can Use Context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data (Android impl)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Presentation (UI)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "DI / Framework",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ViewModel @Stable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/ioannisa/AppFunctions-Demo-App.git
            KotlinCode(
                code = """
                    data class JokeDetailsState(
                        val joke: Joke? = null,
                        val loading: Boolean = false
                    )

                    sealed interface JokeDetailsIntent {
                        data object ToggleFavorite: JokeDetailsIntent
                        data object GoBack: JokeDetailsIntent
                    }

                    sealed interface JokesDetailsEvent {
                        data object GoBack: JokesDetailsEvent
                    }

                    @Stable
                    class JokesDetailsViewModel(
                        joke: Joke,
                        private val repository: JokesRepository
                    ) : ViewModel() { }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModel CoroutineDispatcher",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Narsuf/Stonks
            KotlinCode(
                code = """
                    //koin
                    
                    //di
                    factory { HomeViewModel(Dispatchers.Default) }
                    
                    class HomeViewModel() : ViewModel(dispatcher) { }
                    
                    //ViewModel.kt
                    abstract class ViewModel(dispatcher: CoroutineDispatcher) {
                        protected val viewModelScope = CoroutineScope(dispatcher + SupervisorJob())
                        open fun onResult(result: Map<String, Any>) {}
                    }
                    
                    internal sealed class AppState {
                        abstract val viewModel: ViewModel

                        data class Home(override val viewModel: HomeViewModel) : AppState()
                        data class Search(override val viewModel: SearchViewModel) : AppState()
                        data class Detail(override val viewModel: DetailViewModel) : AppState()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModel Argument Pass Parameter",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Narsuf/Stonks (factory parameter)
            KotlinCode(
                code = """
                    //Koin using ViewModel Argument Pass Parameter
                    
                    //1
                    @Composable 
                    fun DetailsScreen(
                        id: Int
                    ) {
                        val viewModel = koinViewModel<DetailsViewModel> { parameters0f(id) }
                    }
                    
                    class DetailsViewModel 
                        val id: Int?
                    ) : ViewModel() {
                        viewModelScope.launch { }
                    }
                    
                    val mainModules = module {
                        viewModel { parameters ->
                            DetailsViewModel(
                                getUserUseCase = get(),
                                id = parameters.get()
                            )
                        ｝
                    }
                    
                    //ViewModel Access UI
                    @Composable fun HomeScreen(
                        viewModel: HomeViewModel = koinViewModel(), 
                        onLoadAd : (adType: Ads.TYPE,onDone : (Boolean) -> Unit) -> Unit,
                    }
                    
                    //2
                    factory { (symbol: String) -> DetailViewModel(symbol, get(), get(), Dispatchers.Default) }
                    
                    class DetailViewModel(private val symbol: String)
                    
                    Detail(koin.get { parametersOf("") })
                    """.trimIndent()
            )

            //https://github.com/dreamcodervw/OttCmp.git
            KotlinCode(
                code = """
                    @Composable
                    inline fun <reified T : Any> rememberKoinInject(
                        key: Any? = null,
                        noinline parameters: ParametersDefinition? = null,
                    ): T {
                        val koin = getKoin()
                        return remember(key) {
                            if (parameters != null) koin.get(parameters = parameters) else koin.get()
                        }
                    }

                    private val presentationModule = module {
                        factory { (movieId: Int) -> MovieDetailsViewModel(movieId = movieId)) }
                    }
                    
                    class MovieDetailsViewModel(
                        private val movieId: Int
                    ) : BaseViewModel() { }
                    
                    //Navigation
                    val viewModel: MovieDetailsViewModel = rememberKoinInject(key = s.id) { parametersOf(s.id) }
                    MovieDetailScreen(
                        viewModel = viewModel,
                        onBack = { screen = Screen.Home },
                    )
                            
                    @Composable
                    fun MovieDetailScreen(
                        viewModel: MovieDetailsViewModel,
                        onBack: () -> Unit,
                        modifier: Modifier = Modifier,
                    ) {}
                    """.trimIndent()
            )

            SpanText(
                text = "Koin ViewModel inside ViewModel (Multiple ViewModel)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class ChatViewModelWrapper(private val emergencyId: String) : ViewModel(), KoinComponent { 
                        private val sharedViewModel: SharedChatViewModel by inject { parametersOf(emergencyId)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Koin Constructor DSL",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/ioannisa/AppFunctions-Demo-App.git
            KotlinCode(
                code = """
                    factory<JokesRepository> { new(::JokesRepositoryImpl) }

                    viewModel { new(::JokesListViewModel) }
                    viewModel { parameters -> JokesDetailsViewModel(joke = parameters.get(), repository = get()) }
                    
                    @Stable
                    class JokesDetailsViewModel(
                        joke: Joke,
                        private val repository: JokesRepository
                    ) : ViewModel() { }
                    """.trimIndent()
            )

            SpanText(
                text = "metroViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Inject
                    @ViewModelKey(MarketViewModel::class)
                    @ContributesIntoMap(AppScope::class)
                    @OptIn(ExperimentalCoroutinesApi::class)
                    class MarketViewModel() : ViewModel() {
                    }
                    
                    @Composable
                    fun MarketScreen(
                        viewModel: MarketViewModel = metroViewModel()
                    ) {
                    }
                    """.trimIndent()
            )

            //https://github.com/amitshilo11/menudeldia.git
            KotlinCode(
                code = """
                    class DetailViewModel(private val restaurantId: String) : ViewModel() {
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun RestaurantDetailScreen(restaurantId: String, navController: NavController) {
                        val viewModel = viewModel(key = restaurantId) { DetailViewModel(restaurantId) }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Context Parameters - context(viewModel: ViewModel)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                     kotlin {
                         sourceSets.all {
                             languageSettings {
                                 enableLanguageFeature("ContextReceivers")
                             }
                         }
                     }
                    
                     class UserListViewModel(private val fetchUsers: FetchUsersUseCase): BaseViewModel {
                        val users: StateFlow<LoadingState<List<User>>> = loadState 
                        {
                            fetchUsers()
                        }
                     }
                     
                     context(viewModel: ViewModel)
                     fun <T> loadState(
                        block: suspend () -> T
                     ): StateFlow<LoadingState<T>> {

                         val stateFlow =
                            MutableStateFlow<LoadingState<T>>(LoadingState.Loading)

                         viewModel.viewModelScope.launch {
                            try {
                                 val data = block()
                                 stateFlow.value = LoadingState.Success(data)
                            } catch (e: Throwable) {
                                 stateFlow.value = LoadingState.Error(e)
                            }
                         }

                         return stateFlow
                     }
                     
                     sealed interface LoadingState<out T> {
                        object Loading: LoadingState<Nothing>
                        data class Success<T>(val data: T) : LoadingState<T>
                        data class Error(val error: Throwable) : LoadingState<Nothing>
                     }
                """.trimIndent()
            )

            //https://github.com/AnatoliiChub/HRAM.git
            KotlinCode(
                code = """
                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.viewModelScope
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.coroutines.flow.SharingStarted
                    import kotlinx.coroutines.flow.stateIn

                    private const val STATE_WHILE_SUBSCRIBED_TIMEOUT_MS = 5_000L

                    context(viewModel: ViewModel)
                    fun <T> MutableStateFlow<T>.stateInExt(initialValue: T) = stateIn(
                        scope = viewModel.viewModelScope,
                        started = SharingStarted.WhileSubscribed(STATE_WHILE_SUBSCRIBED_TIMEOUT_MS),
                        initialValue = initialValue,
                    )

                    context(viewModel: ViewModel)
                    fun <T> Flow<T>.stateInExt(initialValue: T) = stateIn(
                        scope = viewModel.viewModelScope,
                        started = SharingStarted.WhileSubscribed(STATE_WHILE_SUBSCRIBED_TIMEOUT_MS),
                        initialValue = initialValue,
                    )
                    
                    //ViewModel
                    private val _uiState = MutableStateFlow(RecordScreenState())
                    val uiState = _uiState.stateInExt(initialValue = RecordScreenState())
                    
                    data class RecordScreenState(
                        val bleNotification: BleNotification = BleNotification.Empty,
                        val trackingStatus: TrackingStatus = TrackingStatus(),
                        val recordingState: RecordingState = RecordingState.Init,
                        val requestBluetooth: Boolean = false,
                        val dialog: RecordScreenDialog? = null,
                    ) {
                        fun chooseHrDeviceDialog(duration: Duration) = this.copy(
                            dialog = RecordScreenDialog.ChooseHRDevice(
                                isLoading = true,
                                loadingDuration = duration
                            )
                        )
                    }

                    sealed class RecordScreenDialog {
                        data class ChooseHRDevice(
                            val isLoading: Boolean = false,
                            val scannedDevices: List<BleDevice> = emptyList(),
                            val loadingDuration: Duration,
                            val isDeviceConfirmed: Boolean = false
                        ) : RecordScreenDialog()

                        data class NameActivity(val activityName: String, val error: StringResource? = null) : RecordScreenDialog()

                        data class DeviceConnectedDialog(val bleDevice: BleDevice) : RecordScreenDialog()

                        data object OpenSettingsDialog : RecordScreenDialog()
                    }

                    fun MutableStateFlow<RecordScreenState>.updateHrDeviceDialogIfExists(
                        updatedDialog: (RecordScreenDialog.ChooseHRDevice) -> RecordScreenDialog.ChooseHRDevice
                    ) = (value.dialog as? RecordScreenDialog.ChooseHRDevice)?.let {
                        update { state -> state.copy(dialog = updatedDialog(it)) }
                    }

                    fun MutableStateFlow<RecordScreenState>.settingsDialog() =
                        update { it.copy(dialog = RecordScreenDialog.OpenSettingsDialog) }

                    fun MutableStateFlow<RecordScreenState>.deviceConnectedDialog(bleDevice: BleDevice) = update {
                        it.copy(
                            trackingStatus = it.trackingStatus.copy(trackHR = true, hrDevice = bleDevice),
                            dialog = RecordScreenDialog.DeviceConnectedDialog(bleDevice)
                        )
                    }

                    fun MutableStateFlow<RecordScreenState>.toggleHrTracking() = this.update {
                        it.copy(trackingStatus = it.trackingStatus.copy(trackHR = it.trackingStatus.trackHR.not(), hrDevice = null))
                    }

                    fun MutableStateFlow<RecordScreenState>.toggleRecordingState() =
                        this.update { it.copy(recordingState = if (it.recordingState.isRecording()) Paused else Recording) }

                    fun MutableStateFlow<RecordScreenState>.stop() = this.update { it.copy(recordingState = RecordingState.Init) }

                    fun MutableStateFlow<RecordScreenState>.requestBluetooth() =
                        this.update { it.copy(requestBluetooth = true, dialog = null) }

                    fun MutableStateFlow<RecordScreenState>.indications(bleNotification: BleNotification) =
                        this.update { it.copy(bleNotification = bleNotification) }

                    fun MutableStateFlow<RecordScreenState>.updateHrDeviceDialogConnecting() =
                        this.updateHrDeviceDialogIfExists { it.copy(isDeviceConfirmed = true, isLoading = true) }

                    val MutableStateFlow<RecordScreenState>.isRecording: Boolean get() = value.recordingState.isRecording()
                    """.trimIndent()
            )

            SpanText(
                text = "Good by viewmodel private mutableState set (new field)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    sealed class RunStatus<T>(
                        val data: T? = null,
                        val message: String? = null
                    ) {
                        class Idle<T> : RunStatus<T>()
                        class Success<T>(data: T) : RunStatus<T>(data = data)
                        class Error<T>(errorMessage: String) : RunStatus<T>(message = errorMessage)
                        class Loading<T> : RunStatus<T>()
                    }
                    
                    import androidx.lifecycle.ViewModel
                    import androidx.compose.runtime.* 

                    class AddAreaViewModel(
                    ): ViewModel() {

                        //1
                        var addState by mutableStateOf<RunStatus<ByteArray?>>(RunStatus.Idle())
                            private set
                           
                        //2. semicolon    
                        //Reduce code and then the compiler will know and understand our logic here
                        var addState by mutableStateOf<RunStatus<ByteArray?>>(RunStatus.Idle()); private set 
                           
                        //3   
                        var username by mutableStateOf("")
                           private set   
                         
                        //4
                        var accessToken: String? = null
                           private set
                    }
                    """.trimIndent()
            )

            //Now you can define a changeable backing field right inside the property.
            SpanText(
                text = "field (Good by Underscore and private set)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //KMP Each Module to Add
                    kotlin {
                        androidTarget {
                            compilerOptions {
                                jvmTarget.set(JvmTarget.JVM_11)
                            }
                        }

                        sourceSets.all { languageSettings.optIn("ExplicitBackingFields") }

                        targets.configureEach {
                            compilations.configureEach {
                                compileTaskProvider.get().compilerOptions {
                                    freeCompilerArgs.addAll("-Xexpect-actual-classes", "-Xcontext-parameters")
                                }
                            }
                        }
                    }

                    //Explicit backing fields 
                    
                    //1
                    class DetailsViewModel() : ViewModel() {
                        val state: StateFlow <DetailsUiState>
                            field = MutableStateFlow<DetailsUiState>(DetailsUiState.Loading)
                    }
                    
                    //2
                    val city: StateFlow<String>
                        field = MutableStateFlow("")

                    fun updateCity(newCity: String) {
                        // Smart casting works automatically
                        city.value = newCity
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Backing property (mutable, private)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class ShoppingCart {

                        // Backing property (mutable, private)
                        private val _items = mutableListOf<String>()

                        // Public read-only view
                        val items: List<String>
                            get() = _items

                        fun addItem(item: String) {
                            _items.add(item)
                        }

                        fun removeItem(item: String) {
                            _items.remove(item)
                        }
                    }

                    fun main() {
                        val cart = ShoppingCart()
                        cart.addItem("Apple")
                        cart.addItem("Banana")
                        println(cart.items) // [Apple, Banana]

                        cart.removeItem("Apple")
                        println(cart.items) // [Banana]
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModelStoreOwner",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Activity we use (ComponentActivity to be exact) inherits ViewModelStoreOwner and directly allocates and releases the ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModelStore is a data structure (Map<K, V>) that stores ViewModel, and ViewModelStoreOwner manages it. In fact, when we initialize the ViewModel, it is already stored internally in the ViewModelStore.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Typically, when you declare a ViewModel, you can see that an instance of that ViewModel is loaded through the ViewModelProvider as follows.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                            val viewModel = ViewModelProvider(this).get(MainViewModel::class.java)
                        """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you pass ViewModelStoreOwner to ViewModelProvider, it finds the ViewModelStore that matches the owner and returns the correct ViewModel that matches the key. Below is a code that briefly expresses the core logic of loading ViewModel through ViewModelProvider.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                            public open operator fun <T : ViewModel> get(key: String, modelClass: Class<T>): T {
                                val viewModel = store[key]

                                if (modelClass.isInstance(viewModel)) {
                                    (factory as? OnRequeryFactory)?.onRequery(viewModel!!)
                                    return viewModel as T
                                }
                                val extras = MutableCreationExtras(defaultCreationExtras)
                                extras[VIEW_MODEL_KEY] = key

                             return try factory.create(modelClass, extras).also { store.put(key, it) }
                            }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "As you can see from the code above, the key in ViewModelStore is used to check whether there is a ViewModel and decide whether to reuse it or create a new one. Since the canonicalName of the model class is used internally as the key value, an IllegalArgumentException occurs when a local or anonymous class is used.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How is ViewModel saved during configuration changes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModelStore",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModelStore acts like a safe place.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It holds your ViewModel when the system destroys your Activity or Fragment.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When the system recreates them, the same ViewModelStore is reused (The old ViewModel is still there).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModelProvider",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When you request a ViewModel, ViewModelProvider checks the ViewModelStore first, If the ViewModel exists, it returns it, If not, it creates a new one.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }


            SpanText(
                text = "How does ViewModel survive configuration changes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel does not magically survive configuration changes. It survives because it is built on top of Android’s non-configuration instance mechanism.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Activity and ViewModel Are Created",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The Activity is instantiated.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "A ViewModel is requested using ViewModelProvider",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The ViewModel instance is stored inside a ViewModelStore",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The ViewModelStore is associated with the Activity’s non-configuration state, not with a specific Activity instance",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "At this point, the ViewModel lives outside the Activity lifecycle.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "User Rotates the Device",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Android detects a configuration change",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The current Activity instance must be destroyed",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Android begins tearing down the Activity",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Android Checks Why the Activity Is Being Destroyed",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Before destroying the Activity, Android checks whether this destruction is caused by a configuration change.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Internally, this is determined using:isChangingConfiguration()",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Ul {
                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "For rotation → returns true",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "For a real finish (finish() / back press) → returns false",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }
                            }
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Non-Configuration Objects Are Retained",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Android retains non-configuration instances",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The ViewModelStore is saved as a non-configuration object",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The Activity instance itself is destroyed",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A New Activity Instance Is Created",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "A new Activity instance is created",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Android reattaches the retained non-configuration objects",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "ViewModelProvider looks inside the ViewModelStore",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "An existing ViewModel is found",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The same ViewModel instance is returned (state preserved)",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }
            }

            SpanText(
                text = "How does ViewModel survive configuration changes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The viewModels() function returns a Lazy<T> instance, which serves as a lazy property delegate.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It accepts a single parameter - a factoryProducer. If it’s specified, the ViewModelProvider.Factory returned by this lambda will be used to create a ViewModel instance. If not, the default one will be used.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The function returns an instance of the ViewModelLazy class which is an implementation of the Lazy interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "viewModelLazy’s constructor takes three parameters",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Represents a class of the ViewModel we want to create an instance of. The third one is a lambda that returns a ViewModelProvider.Factory. It’s the same one as the one passed to the viewModels() function (or a default one).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It’s a lambda that returns a ViewModelStore. Here, a lambda is passed that returns a viewModelStore variable. Where is this variable coming from?",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "viewModels() function is an extension function on the ComponentActivity class. So when we call viewModelStore in Kotlin, we effectively invoke the getViewModelStore() method from the ComponentActivity (written in Java) that returns its member variable called mViewModelStor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel is tied to the lifecycle of an Activity or Fragment. It is retained as long as the lifecycle scope is active. Unlike a typical class, the ViewModel instance is not destroyed during configuration changes (e.g., screen rotation).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When user rotates screen, your activity/fragment’s first onDistroy and the onCreate will get called, we will be initializing view model with viewmodelprovider in onCreate only.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Every time it’s creating a new ViewModel Provider, but if you see first argument ,its activity.getViewModelStore() , which means there is a store for viewmodel in android.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModelStore is an object which contains a HashMap of String and ViewModel.key is the default key and value is viewmodel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "getViewModelStore() method from the ComponentActivity, we can notice that it calls another method called ensureViewModelStore() before returning its member instance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If configuration changes happened, if already a viewmodelstore is available then it will return that instance else a new viewmodelstore is created. (If it is, we restore the previous ViewModelStore (if there is any, otherwise, it creates a new one) using the getLastNonConfigurationInstance() method. This method returns an instance of the NonConfigurationInstances class that’s defined. Retrieve the non-configuration instance data that was previously returned by onRetainNonConfigurationInstance())",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is how viewmodel persists on screen rotation. And when finish is called it activity will call viewmodel.clear(). A class that implements it indicates to the outside world that it owns an instance of the ViewModelStore. A responsibility of an implementation of this interface is to retain owned ViewModelStore during the configuration changes and call ViewModelStore#clear(), when this scope is going to be destroyed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How to handle configuration changes?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handling configuration changes in is essential for maintaining a smooth user experience, particularly during events like screen rotations, locale changes, switching between dark and light modes, and adjustments to font size or weight. By default, the Android system restarts an activity when these changes occur, potentially resulting in the loss of transient UI state. To handle these changes effectively, consider the following strategies:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Save and Restore UI State: Implement the onSaveInstanceState() and onRestoreInstanceState() methods to preserve and restore UI state during activity recreation. This ensures that users return to the same state after a configuration change.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Jetpack ViewModel: Leverage the ViewModel class to store UI-related data that survives configuration changes. ViewModel objects are designed to outlive activity recreation, making them ideal for managing data during such events.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Handle Configuration Changes Manually: If your application doesn’t need to update resources during specific configuration changes and you want to avoid activity restarts declare the configuration changes your activity handles in the AndroidManifest.xml using the android:configChanges attribute. Then, override the onConfigurationChanged() method to manage these changes manually.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Utilize rememberSaveable in Jetpack Compose: In Jetpack Compose, you can use rememberSaveable to save UI states across configuration changes. It works similarly to onSaveInstanceState() but is Compose-specific and helps keep composable state consistent.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Does ViewModel survive process death?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "No. A ViewModel survives configuration changes while its owner remains in scope. When Android kills the app process, that ViewModel disappears.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Here’s how I would design a search screen:",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "ViewModel → holds the current screen state.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "SavedStateHandle → saves small restoration inputs, such as the search query and selected filter.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Repository → reloads results using those restored inputs.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Persistent storage → keeps data that must survive app restarts.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The key decision: save enough information to rebuild the screen. Avoid putting the entire API response into saved state.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SavedStateHandle supports restoration after system-initiated process death. It isn’t a replacement for durable storage.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel handles configuration changes. For process recreation, I restore lightweight inputs through SavedStateHandle and rebuild the screen from the repository. Data users expect to keep belongs in persistent storage.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A screen that survives rotation has passed one test. Recovery after process death is another.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "How Does Kotlin Handle Coroutines in ViewModel?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel uses a special CoroutineScope called viewModelScope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does Compose handle lifecycle?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "viewModelScope",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable functions should not perform long-running operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ViewModel vs SavedStateHandle vs Repository",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel: Holds in-memory UI data (temporary).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SavedStateHandle: Keeps small UI state even after process death.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Repository: Manages data sources (DB, network, cache).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "AndroidViewModel (UI + Non UI Task, Heavier)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Same as ViewModel, but it gives access to the Application context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Useful when you need to access Room databases, SharedPreferences, or resources that require context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Constructor requires an Application instance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "savedStateHandle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Important consideration is how these two options work with SavedStateHandle. It is important because this is where you can store small pieces of the state that need to survive process death. Process death happens when the device your app is running on terminates your app’s process when it is open in the background to free up resources for other activities the user is focused on.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "savedStateHandle is a convenient way to store and retrieve data associated with a particular navigation back stack entry. It allows you to save data in one screen and retrieve it in another, making it a flexible solution for passing data between composable screens.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You need to pass complex data like Parcelable objects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to ensure data persistence during configuration changes, like screen rotation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It works alongside the SavedStateViewModelFactory or AbstractSavedStateViewModelFactory to persist and restore values using a Bundle, typically tied to onSaveInstanceState().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "However, it does not guarantee persistence across process death unless the data is stored explicitly in persistent storage.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SavedStateHandle lives inside ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Navigation arguments",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Screen-level parameters",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Filters / search queries",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Anything that affects data loading",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SavedStateHandle is a form of persistent storage. When used in your ViewModel, it behaves no differently than an in-memory HashMap. It only persists the data as a Bundle when the onSaveInstanceState of the Activity is called during process death. The data won’t be persisted when the user kills the app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }
            }

            SpanText(
                text = "savedStateHandle — Surviving Process Death",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If your app is backgrounded and killed, your ViewModel dies too. You can survive this using SavedStateHandle — part of the AndroidX lifecycle suite.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android enables this through state restoration, where small UI state is saved before the process is killed and restored when the app is recreated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A new Activity instance is created",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The previously saved Bundle is restored",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SavedStateRegistry is restored before ViewModel creation",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A new ViewModel instance is created",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SavedStateHandle is injected with restored values",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This works because SavedStateHandle is internally backed by the SavedStateRegistry, which integrates with onSaveInstanceState() and restores state before ViewModel recreation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //StateFlow
                    class MainViewModel(
                      private val savedStateHandle: SavedStateHandle
                    ) : ViewModel() {

                      val flowColor = savedStateHandle.getStateFlow(key = "color", initialValue = OXFFFFFFFF)
                      
                      fun generateNewColor() {
                        val color = Random.nextLong(until = OxFFFFFFFF)
                        savedStateHandle["color"] = color
                       }
                    }
                    
                    //Compose state
                    class MainViewModel(
                      private val savedStateHandle: SavedStateHandle
                    ) : ViewModel() {

                      val composeColor by savedStateHandle.saveable {
                        mutableStateOf(OXFFFFFFFF)
                      }
                      
                      fun generateNewColor() {
                        val color = Random.nextLong(until = OxFFFFFFFF)
                        composeColor = color
                       }
                    }
                    """.trimIndent()
            )

            //https://github.com/patrykandpatrick/liftapp.git
            KotlinCode(
                code = """
                    //1
                    import kotlin.properties.ReadWriteProperty
                    import kotlin.reflect.KProperty

                   class SavedStateHandleDelegate<T>(private val key: String, private val defaultValue: T) :
                        ReadWriteProperty<SavedStateHandleViewModel, T> {

                        override fun getValue(thisRef: SavedStateHandleViewModel, property: KProperty<*>) =
                            thisRef.savedStateHandle.get<T>(key = key) ?: defaultValue

                        override fun setValue(thisRef: SavedStateHandleViewModel, property: KProperty<*>, value: T) {
                            thisRef.savedStateHandle[key] = value
                        }
                    }
                   
                    interface SavedStateHandleViewModel {
                        val savedStateHandle: SavedStateHandle

                        fun <T : Any> saveable(key: String, saver: Saver<T, out Any> = autoSaver(), init: () -> T) =
                            savedStateHandle.saveable(key = key, saver = saver, init = init)
                    }
                    
                    //2
                    inline fun <T> SavedStateHandle.update(key: String, block: (T?) -> T?): T? {
                        val updated = block(get(key))
                        set(key, updated)
                        return updated
                    }
                    """.trimIndent()
            )

            //https://github.com/wshtaits/GetLike.git
            KotlinCode(
                code = """
                    import androidx.compose.runtime.MutableState
                    import androidx.compose.runtime.mutableStateListOf
                    import androidx.compose.runtime.mutableStateOf
                    import androidx.compose.runtime.saveable.Saver
                    import androidx.compose.runtime.saveable.autoSaver
                    import androidx.compose.runtime.snapshots.SnapshotStateList
                    import androidx.lifecycle.SavedStateHandle
                    import androidx.lifecycle.viewmodel.compose.SavedStateHandleSaveableApi
                    import androidx.lifecycle.viewmodel.compose.saveable
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.serialization.KSerializer
                    import kotlinx.serialization.json.Json
                    import kotlin.properties.PropertyDelegateProvider
                    import kotlin.properties.ReadOnlyProperty
                    import kotlin.properties.ReadWriteProperty
                    import kotlin.reflect.KProperty

                    @OptIn(SavedStateHandleSaveableApi::class)
                    inline fun <reified T> SavedStateHandle.saveableStateFlow(
                        initialValue: T,
                    ): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, MutableStateFlow<T>>> =
                        saveable(
                            saver = Saver(
                                save = { original -> Json.encodeToString(original.value) },
                                restore = { saved -> MutableStateFlow(Json.decodeFromString(saved)) },
                            ),
                            init = { MutableStateFlow(initialValue) },
                        )

                    @OptIn(SavedStateHandleSaveableApi::class)
                    internal fun <T> SavedStateHandle.saveableStateOf(
                        initialValue: () -> T,
                        serializer: KSerializer<T>,
                    ): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, MutableState<T>>> =
                        saveable(
                            saver = Saver(
                                save = { original -> Json.encodeToString(serializer, original.value) },
                                restore = { saved -> mutableStateOf(Json.decodeFromString(serializer, saved)) },
                            ),
                            init = { mutableStateOf(initialValue()) },
                        )

                    @OptIn(SavedStateHandleSaveableApi::class)
                    inline fun <reified T> SavedStateHandle.saveableStateListOf(
                        vararg initialValues: T,
                    ): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, SnapshotStateList<T>>> =
                        saveable(
                            saver = Saver(
                                save = { original -> Json.encodeToString(original.toList()) },
                                restore = { saved -> mutableStateListOf(*Json.decodeFromString<List<T>>(saved).toTypedArray()) },
                            ),
                            init = { mutableStateListOf(*initialValues) },
                        )

                    @OptIn(SavedStateHandleSaveableApi::class)
                    inline fun <reified T> SavedStateHandle.saveableMutableListOf(
                        vararg initialValues: T,
                    ): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, MutableList<T>>> =
                        saveable(
                            saver = Saver(
                                save = { original -> Json.encodeToString(original) },
                                restore = { saved -> mutableListOf(*Json.decodeFromString<List<T>>(saved).toTypedArray()) },
                            ),
                            init = { mutableListOf(*initialValues) },
                        )

                    @OptIn(SavedStateHandleSaveableApi::class)
                    inline fun <reified T> SavedStateHandle.saveableMutableSetOf(
                        vararg initialValues: T,
                    ): PropertyDelegateProvider<Any?, ReadOnlyProperty<Any?, MutableSet<T>>> =
                        saveable(
                            saver = Saver(
                                save = { original -> Json.encodeToString(original) },
                                restore = { saved -> mutableSetOf(*Json.decodeFromString<List<T>>(saved).toTypedArray()) },
                            ),
                            init = { mutableSetOf(*initialValues) },
                        )

                    @OptIn(SavedStateHandleSaveableApi::class)
                    fun <T : Any> SavedStateHandle.saveable(
                        initialValue: T,
                    ): PropertyDelegateProvider<Any?, ReadWriteProperty<Any?, T>> =
                        PropertyDelegateProvider { thisRef, property ->
                            var mutableState by saveable(
                                stateSaver = autoSaver(),
                                init = { mutableStateOf(initialValue) }
                            )
                            object : ReadWriteProperty<Any?, T> {
                                override fun getValue(thisRef: Any?, property: KProperty<*>): T =
                                    mutableState

                                override fun setValue(thisRef: Any?, property: KProperty<*>, value: T) {
                                    mutableState = value
                                }
                            }
                        }
                    """.trimIndent()
            )

            //https://github.com/kroegerama/kmp-kaiteki
            KotlinCode(
                code = """
                    import androidx.lifecycle.SavedStateHandle
                    import kotlin.properties.ReadWriteProperty
                    import kotlin.reflect.KProperty

                    public fun <T : Any> SavedStateHandle.field(
                        key: String? = null,
                        init: () -> T,
                    ): ReadWriteProperty<Any?, T> {
                        return SavedStateHandleDelegate(
                            savedStateHandle = this,
                            key = key,
                            init = init
                        )
                    }

                    private class SavedStateHandleDelegate<T : Any>(
                        private val savedStateHandle: SavedStateHandle,
                        private val key: String?,
                        private val init: () -> T,
                    ) : ReadWriteProperty<Any?, T> {

                        private fun createDefaultKey(thisRef: Any?, property: KProperty<*>): String {
                            val classNamePrefix = if (thisRef != null) thisRef::class.qualifiedName + "." else ""
                            return classNamePrefix + property.name
                        }

                        override fun getValue(thisRef: Any?, property: KProperty<*>): T {
                            val qualifiedKey = key ?: createDefaultKey(thisRef, property)
                            val value = savedStateHandle[qualifiedKey] ?: init().also {
                                savedStateHandle[qualifiedKey] = it
                            }
                            return value
                        }

                        override fun setValue(thisRef: Any?, property: KProperty<*>, value: T) {
                            val qualifiedKey = key ?: createDefaultKey(thisRef, property)
                            savedStateHandle[qualifiedKey] = value
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModels using AutoCloseable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Android’s ViewModel class supports automatic resource cleanup through the AutoCloseable interface. When a ViewModel is cleared, any services that implement AutoCloseable and are passed to the ViewModel constructor will automatically have their close() method invoked.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    interface ItemsService : AutoCloseable {
                        val items: Flow<List<Item>>
                        suspend fun getItems(): List<Item>
                    }
                    
                    //Then implement the close() method
                    class RealItemsService : ItemsService {
                        private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
                        
                        override fun close() {
                            scope.cancel()
                            println("ItemsService closed - resources cleaned up")
                        }
                    }
                    
                    //Passing AutoCloseable Services to a ViewModel
                    class HomeViewModel(
                        private val itemsService: ItemsService,
                        private val customersService: CustomersService,
                        private val invoiceService: InvoiceService
                    ) : ViewModel(
                        itemsService,
                        customersService,
                        invoiceService
                    ) {
  
                    }
                    """.trimIndent()
            )

            //https://github.com/wshtaits/GetLike.git
            KotlinCode(
                code = """
                    import kotlinx.coroutines.CoroutineScope
                    import kotlinx.coroutines.Dispatchers
                    import kotlinx.coroutines.IO
                    import kotlinx.coroutines.Job
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.SharingStarted
                    import kotlinx.coroutines.flow.StateFlow
                    import kotlinx.coroutines.flow.stateIn
                    import kotlinx.coroutines.launch
                    import kotlinx.coroutines.withContext

                    open class Repository(
                        private val coroutineScope: CoroutineScope,
                    ) {

                        protected suspend fun <T> executeIoCatching(
                            onFinally: suspend CoroutineScope.() -> Unit = {},
                            onTry: suspend CoroutineScope.() -> T,
                        ): Result<T> =
                            withContext(Dispatchers.IO) {
                                try {
                                    Result.success(onTry())
                                } catch (t: Throwable) {
                                    Result.failure(t)
                                } finally {
                                    onFinally()
                                }
                            }

                        protected suspend fun <T> executeIo(block: suspend CoroutineScope.() -> T): T =
                            withContext(Dispatchers.IO, block)

                        protected fun <T> Flow<T>.launchCollect(collector: suspend (T) -> Unit): Job =
                            launch { collect(collector) }

                        protected fun launch(block: suspend CoroutineScope.() -> Unit): Job =
                            coroutineScope.launch(block = block)

                        fun <T> Flow<T>.stateIn(initialValue: T): StateFlow<T> =
                            stateIn(
                                scope = coroutineScope,
                                started = SharingStarted.Lazily,
                                initialValue = initialValue,
                            )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModels EmptyCoroutineContext (Kotlin 2.3.0)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/thementalgoose/kmp-flashback.git
            KotlinCode(
                code = """
                    class AllCircuitsViewModel(
                        private val circuitRepository: CircuitRepository,
                        private val coroutineContext: CoroutineContext = EmptyCoroutineContext
                    ): ViewModel() {
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Initial Load 3 Ways",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://proandroiddev.com/where-should-initial-load-logic-live-in-jetpack-compose-bb5b44a1271f#3461
            KotlinCode(
                code = """
                    //1. Initial Load Compose
                    @Composable
                    fun MyScreen(viewModel: MyViewModel = viewModel()) {
                        LaunchedEffect(Unit) {
                            viewModel.onAction(Action.ScreenStarted)
                        }

                    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
                        // ... render uiState
                    }

                    // ViewModel: stays idle until told to start
                    class MyViewModel : ViewModel() {

                        private val _uiState = MutableStateFlow<UiState>(UiState.Idle)

                        val uiState: StateFlow<UiState> = _uiState.asStateFlow()

                        fun onAction(action: Action) {
                            when (action) {
                                Action.ScreenStarted -> viewModelScope.launch { load() }
                                Action.Retry -> viewModelScope.launch { load() }
                            }
                        }

                        private suspend fun load() {
                            _uiState.value = UiState.Loading
                            // fetch data, update _uiState
                        }
                    }

                    sealed interface Action {
                        data object ScreenStarted : Action
                        data object Retry : Action
                    }
                    
                    //2. Initial Load ViewModel()
                    class MyViewModel : ViewModel() {
                        init {
                            viewModelScope.launch { load() }
                        }

                        private suspend fun load() {
                            //fetch data, update state
                        }
                    }
                    
                    //3. .onStart / .onSubscription + stateIn
                    class MyViewModel : ViewModel() {
                        private val retryTrigger = MutableSharedFlow<Unit>(extraBufferCapacity = 1)

                        val uiState: StateFlow<UiState> = retryTrigger
                            .onStart { emit(Unit) }  // auto-trigger on first subscription
                            .transformLatest {
                                emit(UiState.Loading)
                                val result = repository.loadData()
                                emit(result.fold(
                                    onSuccess = { UiState.Success(it) },
                                    onFailure = { UiState.Error(it.message) }
                                ))
                            }
                            .stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5_000),
                                initialValue = UiState.Loading
                            )

                        fun retry() {
                            retryTrigger.tryEmit(Unit)
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect: This composable function runs a block of code in a coroutine when the key changes. In this case, Unit is used as a key, so the code runs only once when the composable is first composed. You want to trigger data fetching after the Composable enters composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "viewModelScope.launch(): This coroutine scope allows launching coroutines tied to the ViewModel's lifecycle, automatically canceling them when the ViewModel is cleared. It is backed by SupervisorJob() + Dispatchers.Main.immediate. It is created lazily the first time it is accessed, and it is stored as a property on the ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel StateFlow: ViewModel along with StateFlow allows you to manage UI-related data in a lifecycle-aware manner. This approach is particularly powerful for observing state changes and handling data that persists across configuration changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //Why use Coordinator/Aggregator pattern in viewmodel?
            SpanText(
                text = "Too Many Parameters constructor in viewmodel trap",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Violates Single Responsibility Principle (SRP)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Heavy Construction Time",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Memory Retention",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Tight Coupling",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Difficult Testing & Mocking",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency Injection (DI) Overhead",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class DashboardViewModel(
                        private val userRepo: UserRepository,
                        private val orderRepo: OrderRepository,
                        private val productRepo: ProductRepository,
                        private val networkMonitor: NetworkMonitor,
                        private val dispatcher: CoroutineDispatcher,
                        private val logger: Logger,
                        private val analytics: Analytics,
                        private val remoteConfig: RemoteConfig,
                        private val preference: UserPreferences
                    ) : ViewModel()
                    """.trimIndent()
            )

            SpanText(
                text = "How to fix it",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Group Related Logic into Use Cases",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use Aggregator (merges data from multiple sources) or “Coordinator” (handles navigation and user flow) Patterns",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lazy or Deferred Initialization",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Limit Global Service Injection",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Factory (Koin)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Factory Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Factory Pattern is mainly used to decouple object creation from object usage.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //Factory is used at layer boundaries
            //UI creates ViewModel via factory
            //You can’t use constructor injection directly
            //UseCases depend on repository interfaces

            KotlinCode(
                code = """
                    //Aggregator / Coordinator Pattern
                    class DashboardViewModel(
                        private val userRepo: UserRepository,
                        private val orderRepo: OrderRepository,
                        private val productRepo: ProductRepository,
                        private val analytics: Analytics
                    ) : ViewModel() {

                        val dashboardState = combine(
                            userRepo.userFlow,
                            orderRepo.orderFlow,
                            productRepo.productFlow
                        ) { user, orders, products ->
                            DashboardState(user, orders, products)
                        }.stateIn(viewModelScope, SharingStarted.Lazily, DashboardState.Empty)
                    }
                    
                    //With Use Cases Inside Coordinator
                    class DashboardCoordinator(
                        private val fetchUserUseCase: FetchUserUseCase,
                        private val fetchOrdersUseCase: FetchOrdersUseCase,
                        private val fetchProductsUseCase: FetchProductsUseCase
                    ) {
                        fun loadDashboard(): Flow<DashboardState> = combine(
                            fetchUserUseCase(),
                            fetchOrdersUseCase(),
                            fetchProductsUseCase()
                        ) { user, orders, products ->
                            DashboardState(user, orders, products)
                        }
                    }
                    
                    //Coordinator Pattern
                    class PaymentCoordinator(
                        private val paymentRepo: PaymentRepository,
                        private val validator: PaymentValidator,
                        private val analytics: Analytics
                    ) {
                        suspend fun executePayment(order: Order): Result {
                            if (!validator.validate(order)) return Result.Error("Invalid order")
                            analytics.track("Payment started")
                            return paymentRepo.pay(order)
                        }
                    }
                    
                    class PaymentViewModel(
                        private val coordinator: PaymentCoordinator
                    ) : ViewModel() {
                        fun pay(order: Order) = viewModelScope.launch {
                            val result = coordinator.executePayment(order)
                            // handle result
                        }
                    }
                    
                    //Best way using Aggregator
                    class HomeAggregator(
                        private val userRepo: UserRepository,
                        private val blogRepo: BlogRepository,
                        private val statsRepo: StatsRepository
                    ) {
                        fun homeUiState(): Flow<HomeUiState> =
                            combine(
                                userRepo.userFlow,
                                blogRepo.latestBlogs(),
                                statsRepo.appStats()
                            ) { user, blogs, stats ->
                                HomeUiState(user, blogs, stats)
                            }
                    }
                    
                    class HomeViewModel(
                        private val aggregator: HomeAggregator
                    ) : ViewModel() {
                        val homeUiState = aggregator.homeUiState()
                    }
                    
                    //koin di
                    val dashboardModule = module {
                        single { UserRepository(get()) }
                        single { OrderRepository(get()) }
                        single { DashboardCoordinator(get(), get()) }
                        viewModel { DashboardViewModel(get(), get()) }
                    }
                    """.trimIndent()
            )

            //https://github.com/taetae98coding/Diary-App.git
            KotlinCode(
                code = """
                    @Module
                    @ComponentScan
                    public class CoroutinesModule {
                        @Single
                        @IoDispatcher
                        internal fun providesIoDispatcher(): CoroutineDispatcher {
                            return Dispatchers.IO
                        }

                        @Single
                        internal fun providesApplicationScope(): CoroutineScope {
                            return applicationScope()
                        }
                    }
                    
                    @Module(includes = [ CoroutinesModule::class ])
                    
                    @KoinViewModel
                    internal class GoldHolidayViewModel(
                        @param:IoDispatcher
                        private val ioDispatcher: CoroutineDispatcher,
                    ) : ViewModel() { } 
                    
                    @KoinViewModel
                    internal class TagListViewModel(
                        strategy: AccountTagListStrategy,
                        stateHolder: TagListStateHolderTemplate = TagListStateHolderTemplate(strategy = strategy),
                    ) : ViewModel(stateHolder),
                        TagListStateHolder by stateHolder
                    
                    public interface TagListStrategy {
                        public fun pageTag(): Flow<Result<PagingData<Tag>>>
                        public suspend fun fetch(): Result<Unit>
                        public suspend fun finishTag(tagId: Uuid): Result<Unit>
                        public suspend fun deleteTag(tagId: Uuid): Result<Unit>
                        public suspend fun restartTag(tagId: Uuid): Result<Unit>
                        public suspend fun restoreTag(tagId: Uuid): Result<Unit>
                    }

                    @Factory
                    internal class AccountTagListStrategy(
                        private val pageTagUseCase: PageTagUseCase,
                        private val finishTagUseCase: FinishTagUseCase,
                        private val deleteTagUseCase: DeleteTagUseCase,
                        private val restartTagUseCase: RestartTagUseCase,
                        private val restoreTagUseCase: RestoreTagUseCase,
                    ) : TagListStrategy { }
                    
                    
                    public class TagListStateHolderTemplate(
                        private val strategy: TagListStrategy,
                        private val coroutineScope: CoroutineScope = MainScope(),
                    ) : TagListStateHolder,
                        AutoCloseable {
                        
                        }
                    """.trimIndent()
            )

            //2. https://github.com/droidwithme/product-catalog.git
            KotlinCode(
                code = """
                    public sealed interface Result<out T> {
                        public data class Success<T>(val value: T) : Result<T>
                        public data class Error(val exception: Throwable) : Result<Nothing>
                        public data object Loading : Result<Nothing>
                    }

                    public fun <T> Flow<T>.asResult(): Flow<Result<T>> =
                        map<T, Result<T>> { value -> Result.Success(value = value) }
                            .onStart { emit(Result.Loading) }
                            .catch { exception -> emit(Result.Error(exception = exception)) }
                    
                    @OptIn(kotlinx.coroutines.FlowPreview::class)
                    @Stable
                    internal class HomeViewModel(
                        private val getProductsUseCase: GetProductsUseCase,
                        private val searchProductsUseCase: SearchProductsUseCase
                    ) : ViewModel() {

                        private val _error = MutableStateFlow<Throwable?>(null)
                        private val _products = MutableStateFlow<List<Products>>(emptyList())
                        private val _searchQuery = MutableStateFlow("")
                        private val _searchActive = MutableStateFlow(false)

                        private var currentPage = 0
                        private var totalPages = 1
                        private var isFetching = false

                        @OptIn(ExperimentalCoroutinesApi::class)
                        private val searchResults = _searchQuery
                            .debounce(300)
                            .flatMapLatest { query ->
                                if (query.isNotBlank()) {
                                    searchProductsUseCase(SearchProductsUseCase.Param(query))
                                } else {
                                    MutableStateFlow(Result.Success(com.revest.demo.shared.domain.model.PaginatedData(0, emptyList(), 0, 0)))
                                }
                            }
                            .stateIn(viewModelScope, WhileUiSubscribed, Result.Loading)

                        val error: StateFlow<Throwable?> = _error
                        val uiState: StateFlow<HomeViewState> = combine(
                            _products,
                            searchResults,
                            _searchQuery,
                            _searchActive,
                            error
                        ) { products, searchResults, query, active, error ->
                            HomeViewState(
                                productsViewState = products.toViewState(),
                                searchResults = searchResults.toViewStatePaginated(),
                                searchQuery = query,
                                searchActive = active,
                                error = error
                            )
                        }.stateIn(
                            scope = viewModelScope,
                            started = WhileUiSubscribed,
                            initialValue = HomeViewState(),
                        )

                        init {
                            loadMore()
                        }

                        fun onSearchQueryChange(query: String) {
                            _searchQuery.value = query
                        }

                        fun onSearchActiveChange(active: Boolean) {
                            _searchActive.value = active
                        }

                        fun loadMore() {
                            if (isFetching || currentPage >= totalPages) return

                            viewModelScope.launch {
                                isFetching = true
                                getProductsUseCase(param = GetProductsUseCase.Param(limit = 10, skip = currentPage * 10))
                                    .collect { result ->
                                        when (result) {
                                            is Result.Success -> {
                                                val response = result.value
                                                if (response.data.isNotEmpty()) {
                                                    _products.value += response.data
                                                    totalPages = response.totalPages
                                                    currentPage++
                                                }
                                            }
                                            is Result.Error -> {
                                                _error.value = result.exception
                                            }
                                            Result.Loading -> { /* No-op */ }
                                        }
                                        isFetching = false
                                    }
                            }
                        }

                        fun onError(error: Throwable) {
                            _error.tryEmit(value = error)
                        }

                        fun onErrorConsumed() {
                            _error.tryEmit(value = null)
                        }
                    }       
                    """.trimIndent()
            )

            //https://github.com/Quartel-Enterprise/bible-planner-mobile-client.git
            KotlinCode(
                code = """
                    //Factory Koin
                    
                    @OptIn(ExperimentalTime::class)
                    internal class DayUiStateFlowFactory(
                        private val getDayDetails: GetDayDetailsUseCase,
                        private val getBooks: GetBooksUseCase,
                        private val readDateFormatter: ReadDateFormatter,
                        private val editDaySelectableDates: EditDaySelectableDates,
                        private val convertTimestampToDatePickerInitialDate: ConvertTimestampToDatePickerInitialDateUseCase,
                        private val calculateAllChaptersReadStatus: CalculateAllChaptersReadStatusUseCase,
                        private val localDateTimeProvider: LocalDateTimeProvider,
                    ) { }
                     
                    val dayModule = module {
                        factoryOf(::DayUiStateFlowFactory)
                        viewModelOf(::DayViewModel)
                    }
                    
                    @OptIn(ExperimentalTime::class)
                    internal class DayViewModel(
                        savedStateHandle: SavedStateHandle,
                        private val useCases: DayUseCases,
                        private val dayUiStateFlowFactory: DayUiStateFlowFactory,
                        private val deleteRouteNotesMapper: DeleteRouteNotesMapper,
                    ) : ViewModel() { }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.flow.*

                    class FlowOnRepository {
                        fun fetchUsers(): Flow<String> = flow {
                            println("💡Repository emitting on: ${currentCoroutineContext()[CoroutineDispatcher]}")
                            repeat(3) { i ->
                                delay(500) // Simulate network delay
                                val user = "User $i"
                                println("Repository emitting: $user")
                                emit(user)
                            }
                        }.flowOn(Dispatchers.IO) // 👈 Flow upstream runs on IO dispatcher
                    }
                    
                    class FlowOnViewModel : ViewModel() {
                        private val repository = FlowOnRepository()

                        // Expose the Flow from the repository
                        val usersFlow = repository.fetchUsers()

                        init {
                            // Launch a coroutine to collect from the flow
                            // viewModelScope.launch(Dispatchers.IO) {} //Background thread
                            
                            viewModelScope.launch() //Main (UI thread)
                            {
                                println("💡ViewModel collecting on: ${this.coroutineContext[CoroutineDispatcher]}")
                                usersFlow.collect { user ->
                                    println("ViewModel received: $user")
                                }
                            }
                        }
                    }
                    
                    @Composable
                    fun FlowOnScreen() {
                        val viewModel: FlowOnViewModel = viewModel()

                        // LaunchedEffect runs in a coroutine tied to composition (Main dispatcher)
                        LaunchedEffect(Unit) {
                            println("💡UI collecting on: ${this.coroutineContext[CoroutineDispatcher]}")

                            viewModel.usersFlow.collect { user ->
                                println("UI received: $user")
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    ❌ The Seemingly Smart Optimization
                    @Composable
                    fun MessageList(messages: List<Message>) {
                        val searchQuery by searchViewModel.query.collectAsState()
                        
                        // "I'll only recompose when filtered results change!"
                        val filteredMessages by remember(messages, searchQuery) {
                            derivedStateOf {
                                messages.filter { 
                                    it.content.contains(searchQuery, ignoreCase = true) 
                                }
                            }
                        }
                        
                        LazyColumn {
                            items(
                                items = filteredMessages,
                                key = { it.id }
                            ) { message ->
                                MessageCard(message)
                            }
                        }
                    }

                    ✅ The Correct Pattern: ViewModel-Level State
                    class MessageViewModel : ViewModel() {
                        private val _searchQuery = MutableStateFlow("")
                        private val _messages = MutableStateFlow<List<Message>>(emptyList())
                        
                        val filteredMessages: StateFlow<List<MessageUiState>> = combine(
                            _messages,
                            _searchQuery
                        ) { messages, query ->
                            messages
                                .asSequence()  // Avoid intermediate collections
                                .filter { it.content.contains(query, ignoreCase = true) }
                                .map { it.toUiState() }
                                .toList()
                        }
                        .flowOn(Dispatchers.Default)  // Off main thread
                        .stateIn(
                            scope = viewModelScope,
                            started = SharingStarted.WhileSubscribed(5000),
                            initialValue = emptyList()
                        )
                    }

                    @Composable
                    fun MessageList(viewModel: MessageViewModel) {
                        val filteredMessages by viewModel.filteredMessages.collectAsState()
                        
                        LazyColumn {
                            items(
                                items = filteredMessages,
                                key = { it.id }
                            ) { message ->
                                MessageCard(message)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Common ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/Ninjars/LastFleetProtocol
            KotlinCode(
                code = """
                    interface StateProvider<T> {
                        val state: StateFlow<T>
                    }
                    
                    import androidx.compose.runtime.Composable
                    import androidx.compose.runtime.LaunchedEffect
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.MutableSharedFlow

                    interface HasSideEffect<T> {
                        val sideEffect: Flow<T>

                        suspend fun sendSideEffect(sideEffect: T)
                    }

                    class DefaultSideEffect<T> : HasSideEffect<T> {
                        private val _sideEffect = MutableSharedFlow<T>()
                        override val sideEffect: Flow<T> = _sideEffect
                        override suspend fun sendSideEffect(sideEffect: T) {
                            _sideEffect.emit(sideEffect)
                        }
                    }

                    @Composable
                    fun <T> HasSideEffect<T>.HandleSideEffect(handler: (T) -> Unit) {
                        LaunchedEffect(sideEffect) {
                            sideEffect.collect { handler(it) }
                        }
                    }
                    
                    import androidx.lifecycle.ViewModel
                    import java.util.function.Consumer


                    abstract class ViewModelContract<Intent, ViewState, SideEffect>
                        : ViewModel(),
                        StateProvider<ViewState>,
                        Consumer<Intent>,
                        HasSideEffect<SideEffect> by DefaultSideEffect()
                        
                    sealed interface LandingIntent {
                        data class ToggleMusicClicked(val setEnabled: Boolean) : LandingIntent
                        data class ToggleSoundEffectsClicked(val setEnabled: Boolean) : LandingIntent
                        data object ShowSettingsClicked : LandingIntent
                        data object PlayClicked : LandingIntent
                        data object ShipBuilderClicked : LandingIntent
                        data object ScenarioBuilderClicked : LandingIntent
                    }

                    data class LandingState(
                        val musicEnabled: Boolean,
                        val soundEffectsEnabled: Boolean,
                        val hasSaveGame: Boolean?,
                        val kubriko: Kubriko,
                        /**
                         * Item B: dev-only `Scenario Builder (dev)` link visibility. Frozen at
                         * VM construction from `DevToolsGate.isAvailable` — the gate doesn't
                         * change at runtime within a process, so a static snapshot is sufficient.
                         */
                        val canShowDevTools: Boolean,
                    )

                    sealed interface LandingSideEffect {
                        data object StartNewGame : LandingSideEffect
                        data object GoToSettings : LandingSideEffect
                        data object GoToShipBuilder : LandingSideEffect
                        data object GoToScenarioBuilder : LandingSideEffect
                    }

                    private data class InternalState(
                        val saveGame: SaveGameState,
                    ) {
                        sealed interface SaveGameState {
                            data object Checking : SaveGameState
                            data object NoSaveGame : SaveGameState
                            data object Data : SaveGameState // TODO: implement save game loading
                        }

                        companion object {
                            val default = InternalState(
                                saveGame = SaveGameState.Checking,
                            )
                        }
                    }

                    @Inject
                    class LandingVM(
                        private val gameStateHolder: GameSessionState,
                        userPreferences: UserPreferences,
                        private val setMusicEnabled: SetMusicEnabled,
                        private val setSoundEffectsEnabled: SetSoundEffectsEnabled,
                        devToolsGate: DevToolsGate,
                    ) : ViewModelContract<LandingIntent, LandingState, LandingSideEffect>() {
                   
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Channel for One-Time Events",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "SharedFlow with replay can re-emit events on configuration changes. StateFlow persists state (which is correct for UI STATE but wrong for EVENTS like show snackbar or navigate).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // ❌ JUNIOR: SharedFlow with replay — event fires AGAIN on rotation
                    class MyViewModel : ViewModel() {
                        private val _events = MutableSharedFlow<UiEvent>(replay = 1)
                        val events = _events.asSharedFlow()

                    fun onTransferSuccess() {
                            viewModelScope.launch {
                                _events.emit(UiEvent.ShowSnackbar("Transfer completed"))
                                // On rotation: new collector receives the replayed event
                                // → Snackbar shows AGAIN even though user already saw it
                            }
                        }
                    }
                    
                    // ✅ SENIOR: Channel - consumed exactly once, no replay
                    class MyViewModel : ViewModel() {
                        private val _events = Channel<UiEvent>(Channel.BUFFERED)
                        val events = _events.receiveAsFlow()
                        // receiveAsFlow: each event is received by ONE collector, then gone
                        // No replay, no re-delivery on config change
                        fun onTransferSuccess(transferId: String) {
                            viewModelScope.launch {
                                _events.send(UiEvent.ShowSnackbar("Transfer completed"))
                                _events.send(UiEvent.Navigate(TransferDetailEntry(transferId)))
                            }
                        }
                    }
                    
                    // Collect in Compose:
                    @Composable
                    fun TransferScreen(viewModel: TransferViewModel = hiltViewModel()) {
                        val snackbarHostState = remember { SnackbarHostState() }
                        LaunchedEffect(Unit) {
                            viewModel.events.collect { event ->
                                when (event) {
                                    is UiEvent.ShowSnackbar -> {
                                        snackbarHostState.showSnackbar(event.message)
                                    }
                                    is UiEvent.Navigate -> {
                                        navController.navigate(event.route)
                                    }
                                }
                            }
                        }
                        Scaffold(snackbarHost = { SnackbarHost(snackbarHostState) }) { /* ... */ }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Immutable Data + Stable Collections",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // ❌ JUNIOR: Mutable list — Compose can't track changes
                    data class HomeState(
                        val transfers: MutableList<Transfer> = mutableListOf()
                    )

                    class HomeViewModel : ViewModel() {
                        private val _state = MutableStateFlow(HomeState())
                        val state = _state.asStateFlow()
                        fun addTransfer(transfer: Transfer) {
                            _state.value.transfers.add(transfer)  // Mutating the SAME list
                            // StateFlow reference didn't change → Compose doesn't recompose!
                            // User sees nothing - the new transfer doesn't appear
                        }
                    }
                    
                    // ✅ SENIOR: Immutable list - new reference on every change
                    data class HomeState(
                        val transfers: List<Transfer> = emptyList()  // Immutable interface
                    )
                    class HomeViewModel : ViewModel() {
                        private val _state = MutableStateFlow(HomeState())
                        val state = _state.asStateFlow()
                        fun addTransfer(transfer: Transfer) {
                            _state.update { currentState ->
                                currentState.copy(
                                    transfers = currentState.transfers + transfer  // NEW list → new reference
                                )
                            }
                            // StateFlow emits new value → Compose detects change → recomposes
                        }
                    }
                    
                    // ✅ BEST: kotlinx.collections.immutable for guaranteed stability
                    import kotlinx.collections.immutable.ImmutableList
                    import kotlinx.collections.immutable.persistentListOf
                    import kotlinx.collections.immutable.toPersistentList
                    
                    data class HomeState(
                        val transfers: ImmutableList<Transfer> = persistentListOf()
                    )
                    fun addTransfer(transfer: Transfer) {
                        _state.update { it.copy(transfers = (it.transfers + transfer).toPersistentList()) }
                    }
                    // ImmutableList is marked @Stable by Compose compiler
                    // Compose KNOWS it's immutable → more aggressive skipping → better performance
                    // WHY THIS MATTERS IN LAZYLISTS:
                    @Composable
                    fun TransferList(transfers: ImmutableList<Transfer>) {
                        LazyColumn {
                            items(transfers, key = { it.id }) { transfer ->
                                TransferRow(transfer)
                                // With ImmutableList: Compose skips recomposition for unchanged items
                                // With MutableList: Compose recomposes ALL items (can't prove stability)
                            }
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/Quartel-Enterprise/bible-planner-mobile-client.git (Factory koin)
//https://github.com/timkaragosian1/WeHungry-Client.git (Base ViewModel)

//https://www.youtube.com/watch?v=5ADwSFac7bA
//https://proandroiddev.com/inside-jetpack-viewmodel-internal-mechanisms-and-multiplatform-design-2625671eaef8
//https://proandroiddev.com/where-should-initial-load-logic-live-in-jetpack-compose-bb5b44a1271f
//https://medium.com/@ahmed.ally2/ways-to-pass-parameters-to-a-viewmodel-b2fbba7c12b7
//https://medium.com/@devabdulkadirali/composition-vs-inheritance-in-android-kotlin-delegation-for-better-architecture-bbef4d1f5dd3
//https://medium.com/@devabdulkadirali/composition-vs-inheritance-in-android-kotlin-delegation-for-better-architecture-bbef4d1f5dd3
//https://softaai.com/the-truth-about-viewmodel-and-remembersavable/
//https://github.com/alexiyous/ramadhan-camp-kmp.git (MVI API Calling, Common ViewModel With Parameter Pass)

//https://www.youtube.com/watch?v=XUvTOAtOPSM //2.11
//https://medium.com/@hsinha610/stop-overriding-oncleared-clean-up-android-viewmodels-with-autocloseable-ff089ab6be6c
